import { useCallback, useEffect, useRef, useState } from 'react'

import useIsMounted from '../../../hooks/useIsMounted'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import { useCartAllowGuest } from '../../cart'
import { endDispensarySession, startDispensarySession } from '../state'
import { useDispensaryUpdateCart } from './useDispensaryUpdateCart'
import { useDispensaryUpdateUser } from './useDispensaryUpdateUser'
import { useShouldUpdateDispensary } from './useShouldUpdateDispensary'
import { useUserDispensary } from './useUserDispensary'

export interface UseDispensaryUpdateSession {
  loading: boolean
  called: boolean
  error: Error | undefined
}

export const useDispensaryUpdateSession = (): UseDispensaryUpdateSession => {
  const [loading, setLoading] = useState(false)
  const [called, setCalled] = useState(false)
  const [error, setError] = useState<Error | undefined>(undefined)
  const isMounted = useIsMounted()
  // The session's count ends once, when it settles or when this page unmounts, whichever is first.
  // Its awaits can outlive the page and never settle (`usePromisify` resolves from an effect), and
  // the next page's session holds `hydrating$` from its own start. A redirect waits for the
  // unmount: ending sooner would let an empty cart send the old page to its recovery URL meanwhile.
  const session = useRef<'idle' | 'running' | 'ended'>('idle')
  const endSession = useCallback(() => {
    if (session.current === 'running') {
      session.current = 'ended'
      endDispensarySession()
    }
  }, [])
  // after the commit, so the next page's session has started and the count never touches zero
  useEffect(() => () => queueMicrotask(endSession), [endSession])

  useCartAllowGuest({ allowGuestCart: true })

  const { shouldUpdateDispensary, refreshClaims } = useShouldUpdateDispensary()
  const { attemptRedirect } = useUserDispensary()
  const [updateUserDispensary] = useDispensaryUpdateUser({ refreshClaims })
  const [updateCartDispensary] = useDispensaryUpdateCart()

  useLogAndCaptureError(error)

  const setErrorIfMounted = useCallback(
    (errorThrown: unknown) => {
      if (isMounted.current) {
        setError(errorThrown instanceof Error ? errorThrown : new Error(`${errorThrown}`))
      }
    },
    [isMounted],
  )

  useEffect(() => {
    if (loading || called || error) {
      return
    }
    let redirected = false
    ;(async () => {
      try {
        // Before `setCalled`, so the children that mount on `called` never see `hydrating$` false
        startDispensarySession()
        session.current = 'running'
        setLoading(true)
        setCalled(true)
        const didRedirect = await attemptRedirect()
        if (didRedirect) {
          redirected = true
          return
        }
        const { shouldUpdateUser, shouldUpdateCart } = await shouldUpdateDispensary()
        /*
         * Update cart and user in parallel:
         * - parallel execution reduces overall processing time
         * - dispensary cart status does not change to `loading` until `updateCartDispensary` is called
         */
        await Promise.all([
          updateUserDispensary({
            shouldUpdateDispensary: shouldUpdateUser,
          }).catch(setErrorIfMounted),
          updateCartDispensary({
            forceClearEScriptParams: shouldUpdateCart,
            shouldClearDispensaryPromoCode: shouldUpdateCart,
          }).catch(setErrorIfMounted),
        ])
      } catch (updateSessionError) {
        setErrorIfMounted(updateSessionError)
      } finally {
        if (!redirected) {
          endSession()
        }
        if (isMounted.current) {
          setLoading(false)
        }
      }
    })()
  }, [
    loading,
    called,
    error,
    updateCartDispensary,
    updateUserDispensary,
    shouldUpdateDispensary,
    attemptRedirect,
    endSession,
    isMounted,
    setErrorIfMounted,
  ])

  return { loading, called, error }
}
